import {
  CalendarChipButton,
  occurrenceKey,
  resolveChipMeta,
} from "../components/CalendarChipButton";
import {
  buildWeekDays,
  isSameDay,
  toISODate,
  todayLocal,
  weekdayFullLabel,
} from "../lib/calendarDates";
import { monthDayLabel } from "../lib/recordsCopy";
import type { ScheduleOccurrence } from "../types";

export interface CalendarWeekViewProps {
  anchor: Date;
  occurrences: ScheduleOccurrence[];
  assignmentDateById: Map<string, string>;
  movedFromByAssignmentId: Map<string, boolean>;
  thisWeekDays: Date[];
  canWrite: boolean;
  onTapOccurrence: (occurrence: ScheduleOccurrence) => void;
  onAddForDay: (dateIso: string) => void;
}

/**
 * §2.12 — desktop week view. Headings + lists, deliberately NOT a table:
 * each of the 7 day columns gets a real `<h3>` ("Tuesday, July 14")
 * followed by a `<ul>` of chip `<li>`s — a 1-row/7-column table would read
 * as "row 1 of 1" to AT, worse than plain heading/list navigation for this
 * shape of content.
 */
export function CalendarWeekView({
  anchor,
  occurrences,
  assignmentDateById,
  movedFromByAssignmentId,
  thisWeekDays,
  canWrite,
  onTapOccurrence,
  onAddForDay,
}: CalendarWeekViewProps) {
  const days = buildWeekDays(anchor);
  const today = todayLocal();

  const byDate = new Map<string, ScheduleOccurrence[]>();
  for (const occ of occurrences) {
    const list = byDate.get(occ.date);
    if (list) list.push(occ);
    else byDate.set(occ.date, [occ]);
  }

  return (
    <div class="calendar-week">
      {days.map((date) => {
        const iso = toISODate(date);
        const dayOccurrences = byDate.get(iso) ?? [];
        const isToday = isSameDay(date, today);
        return (
          <section key={iso} class={`calendar-week-day${isToday ? " is-today" : ""}`}>
            <h3 class="calendar-week-day-heading">{`${weekdayFullLabel(date)}, ${monthDayLabel(iso)}`}</h3>
            {dayOccurrences.length === 0 ? (
              <p class="calendar-week-day-empty">Nothing scheduled.</p>
            ) : (
              <ul class="calendar-week-day-list">
                {dayOccurrences.map((occ) => {
                  const meta = resolveChipMeta(
                    occ,
                    assignmentDateById,
                    movedFromByAssignmentId,
                    thisWeekDays,
                  );
                  return (
                    <li key={occurrenceKey(occ)}>
                      <CalendarChipButton
                        occurrence={occ}
                        moved={meta.moved}
                        movedTargetLabel={meta.movedTargetLabel}
                        onTap={onTapOccurrence}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            {canWrite ? (
              <button type="button" class="calendar-week-day-add" onClick={() => onAddForDay(iso)}>
                + Add for this day
              </button>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
